"use client";

import dynamic from "next/dynamic";
import { useEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, BellRing, Heart, Loader2, Sparkles } from "lucide-react";
import { useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/components/ui/dialog";
import { getRedirectClick } from "@/lib/click";
import {
    APP_LOCAL_STORAGE_REOPEN_CODE_DIALOG,
    APP_LOCAL_STORAGE_REOPEN_CODE_VALUE,
} from "@/lib/constants";
import { useAppContext } from "@/contexts/app-context";

// code-splitting: the sign-in form + Firebase bundle stay in a separate chunk,
// loaded only when an anonymous user chooses to register from this dialog.
const SigninForm = dynamic(
    () =>
        import("@/app/[locale]/(auth)/signin/_components/signin-form").then(
            (mod) => mod.SigninForm,
        ),
    {
        loading: () => <Loader2 className="h-4 w-4 animate-spin" />,
    },
);

type View = "gate" | "signin" | "reveal";

interface CodeCopiedDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    shopName: string;
    websiteUrl: string;
    /** The real code to reveal / copy once the user has passed the gate. */
    selectedCode: string;
    /** Which view to show when the dialog opens: `gate` (anonymous, pre-reveal) or `reveal`. */
    initialView: "gate" | "reveal";
    /** Reveal view: true → user hasn't copied yet (show "copy & go"); false → already copied (just "go"). */
    needsCopy: boolean;
    /** Called when the user chooses "Anonym fortfahren" — lifts the session reveal flag to the parent. Rejects if the anonymous sign-in fails. */
    onContinueAnonymously: () => Promise<void>;
}

export function CodeCopiedDialog({
    open,
    onOpenChange,
    shopName,
    websiteUrl,
    selectedCode,
    initialView,
    needsCopy,
    onContinueAnonymously,
}: CodeCopiedDialogProps) {
    const t = useTranslations("discountCodes");
    const { user, clientId, clientVersion } = useAppContext();
    const router = useRouter();
    const [view, setView] = useState<View>(initialView);
    const [continuingAnonymously, setContinuingAnonymously] = useState(false);
    const prevOpen = useRef(false);

    // Sync the internal view to `initialView` on each open (false→true edge), so
    // every open-trigger lands on the right view regardless of where the previous
    // session left off. In-dialog transitions (gate → signin → reveal) then manage
    // `view` themselves while the dialog stays open.
    useEffect(() => {
        if (open && !prevOpen.current) {
            setView(initialView);
        }
        prevOpen.current = open;
    }, [open, initialView]);

    // Resolve the tracked /click URL eagerly. This dialog renders only client-side,
    // so DonistaClickLink's deferred post-mount URL upgrade — which exists only to
    // avoid SSR hydration mismatches — isn't needed here. Computing it up front
    // guarantees the link points at /click from the first render, so the affiliate
    // click is never lost.
    const trackedHref = useMemo(
        () => getRedirectClick(websiteUrl, user?.id ?? "", clientId, clientVersion),
        [websiteUrl, user?.id, clientId, clientVersion],
    );

    const handleClose = (next: boolean) => {
        onOpenChange(next);
    };

    // Fire-and-forget clipboard write within the click gesture — not awaited, so the
    // anchor's default navigation (open shop in a new tab) is never blocked.
    const copyCode = (code: string) => {
        navigator.clipboard.writeText(code).catch(() => toast.error(t("copyFailed")));
    };

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-[440px] max-h-[90vh] overflow-y-auto">
                {view === "gate" && (
                    <>
                        <DialogHeader>
                            <div className="mx-auto mb-1 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                                <Sparkles className="h-6 w-6 text-primary" />
                            </div>
                            <DialogTitle className="text-center">{t("dialog.gate.title")}</DialogTitle>
                            <DialogDescription className="text-center">{t("dialog.gate.body")}</DialogDescription>
                        </DialogHeader>
                        <ul className="flex flex-col gap-2.5 rounded-lg bg-muted/60 px-4 py-3 text-sm">
                            <li className="flex items-center gap-2.5">
                                <BellRing className="h-4 w-4 shrink-0 text-primary" />
                                {t("dialog.gate.benefitCodes")}
                            </li>
                            <li className="flex items-center gap-2.5">
                                <Heart className="h-4 w-4 shrink-0 text-primary" />
                                {t("dialog.gate.benefitDonations")}
                            </li>
                        </ul>
                        <div className="flex flex-col gap-2">
                            <Button size="lg" onClick={() => setView("signin")}>
                                {t("dialog.gate.register")}
                            </Button>
                            <Button
                                variant="ghost"
                                className="text-muted-foreground"
                                disabled={continuingAnonymously}
                                onClick={async () => {
                                    setContinuingAnonymously(true);
                                    try {
                                        await onContinueAnonymously();
                                        setView("reveal");
                                    } catch {
                                        toast.error(t("anonSignInFailed"));
                                    } finally {
                                        setContinuingAnonymously(false);
                                    }
                                }}
                            >
                                {t("dialog.gate.later")}
                            </Button>
                        </div>
                    </>
                )}

                {view === "signin" && (
                    <>
                        <DialogHeader>
                            <DialogTitle>{t("dialog.register")}</DialogTitle>
                        </DialogHeader>
                        <SigninForm
                            onEmailSignInStart={(returnUrl) => {
                                // The email flow navigates away to /finish-email-signup and back,
                                // destroying this dialog's local state. Persist the return path AND
                                // the selected code (in lockstep) so the shop page can reopen the
                                // dialog on the reveal view with the right code. Only the email path
                                // reaches here — Google/Apple use popups that never navigate away.
                                localStorage.setItem(APP_LOCAL_STORAGE_REOPEN_CODE_DIALOG, returnUrl);
                                localStorage.setItem(APP_LOCAL_STORAGE_REOPEN_CODE_VALUE, selectedCode);
                            }}
                            onClose={() => {
                                // After a popup sign-in, advance to the reveal view (the user is now
                                // logged in but hasn't copied). router.refresh() repopulates `user`
                                // from the freshly set session cookie.
                                setView("reveal");
                                router.refresh();
                            }}
                        />
                        <Button
                            type="button"
                            variant="ghost"
                            className="w-full text-muted-foreground"
                            onClick={() => setView("gate")}
                        >
                            <ArrowLeft className="h-4 w-4" />
                            {t("dialog.back")}
                        </Button>
                    </>
                )}

                {view === "reveal" && (
                    <>
                        <DialogHeader>
                            <DialogTitle>{needsCopy ? t("dialog.reveal.title") : t("dialog.title")}</DialogTitle>
                            <DialogDescription>
                                {needsCopy ? t("dialog.reveal.body") : t("dialog.body")}
                            </DialogDescription>
                        </DialogHeader>
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-center bg-muted rounded-lg border px-4 py-3">
                                <code className="text-base font-mono font-bold tracking-wider">
                                    {selectedCode}
                                </code>
                            </div>
                            <Button
                                asChild
                                size="lg"
                                onClick={() => {
                                    if (needsCopy) copyCode(selectedCode);
                                    handleClose(false);
                                }}
                            >
                                <a href={trackedHref} target="_blank" rel="noopener noreferrer">
                                    {needsCopy
                                        ? t("dialog.copyAndGo", { shopName })
                                        : t("dialog.continueToShop", { shopName })}
                                </a>
                            </Button>
                            {(!user || user.anonymous) && (
                                <>
                                    <p className="text-sm text-muted-foreground text-center">
                                        {t("dialog.registerHint")}
                                    </p>
                                    <Button variant="outline" onClick={() => setView("signin")}>
                                        {t("dialog.register")}
                                    </Button>
                                </>
                            )}
                        </div>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}
